import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabase'
import {
  t, Stagger, PageHead, PageTitle, PageSub, ChipBar, Chip,
  ListItem, ItemTitle, ItemSub, ItemMeta, Pill, Loading, Empty, Cluster, IconBtn, Input, Btn, fadeUp,
} from './ui'
import { StatusLabels, StatusTone, STATUS_ORDER, CATEGORIES, categoriesDe, correspondRecherche, fmtDate, fmtDateTime, repartitionVues } from './devisUtils'

const JOUR_MS = 864e5
const RECENT_MS = 7 * JOUR_MS   // fenêtre du filtre « consultés récemment »
const FRAIS_MS = 30 * JOUR_MS   // au-delà, une consultation n'est plus signalée

/* « il y a 3 h », « hier », « il y a 5 j » */
function depuis(iso) {
  const ms = Date.now() - new Date(iso)
  const h = Math.floor(ms / 36e5)
  if (h < 1) return "à l'instant"
  if (h < 24) return `il y a ${h} h`
  const j = Math.floor(ms / JOUR_MS)
  if (j === 1) return 'hier'
  if (j < 31) return `il y a ${j} j`
  const m = Math.floor(j / 30)
  return `il y a ${m} mois`
}

const ItemRow = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
`
const ItemSide = styled.div`
  display: flex; flex-direction: column; align-items: flex-end; gap: 5px; flex-shrink: 0;
`
const ViewTag = styled.span`
  font-family: ${t.font}; font-size: 0.665rem; font-weight: 540;
  font-variant-numeric: tabular-nums;
  padding: 2px 7px; border-radius: ${t.rXs}; white-space: nowrap;
  color: ${(p) => (p.$new ? t.gold : t.textFaint)};
  background: ${(p) => (p.$new ? t.goldWash : t.surfaceSunk)};
`
/* Consulté par le client — signal d'activité côté client */
const NewFlag = styled.span`
  font-family: ${t.font}; font-size: 0.6rem; font-weight: 560;
  letter-spacing: 0.06em; text-transform: uppercase;
  color: ${t.textSoft}; background: ${t.surfaceSunk};
  padding: 2px 7px; border-radius: ${t.rXs}; white-space: nowrap;
`
/* Jamais ouvert : pastille pleine, le signal le plus fort de la liste */
const TagNeuf = styled.span`
  font-family: ${t.font}; font-size: 0.6rem; font-weight: 640;
  letter-spacing: 0.07em; text-transform: uppercase;
  color: #fff; background: ${t.gold};
  padding: 2px 8px; border-radius: ${t.rXs}; white-space: nowrap;
`
/* Devis envoyé et ouvert par le client après l'envoi */
const TagLu = styled.span`
  font-family: ${t.font}; font-size: 0.6rem; font-weight: 620;
  letter-spacing: 0.06em; text-transform: uppercase;
  color: ${t.green}; background: ${t.greenWash};
  padding: 2px 7px; border-radius: ${t.rXs}; white-space: nowrap;
`
/* Devis envoyé mais jamais ouvert depuis : candidat à une relance */
const TagNonLu = styled.span`
  font-family: ${t.font}; font-size: 0.6rem; font-weight: 560;
  letter-spacing: 0.06em; text-transform: uppercase;
  color: ${t.textFaint}; background: transparent;
  border: 1px dashed ${t.line};
  padding: 1px 7px; border-radius: ${t.rXs}; white-space: nowrap;
`
/* Ouvert mais rien fait : pastille en contour, volontairement plus discrète */
const TagTodo = styled.span`
  font-family: ${t.font}; font-size: 0.6rem; font-weight: 620;
  letter-spacing: 0.07em; text-transform: uppercase;
  color: ${t.amber}; background: transparent;
  border: 1px solid ${t.amber};
  padding: 1px 7px; border-radius: ${t.rXs}; white-space: nowrap;
`
/* La marchandise demandée, rappelée sur la ligne : sans elle on filtre à
   l'aveugle, et c'est souvent la première chose qu'on cherche à savoir. */
const TagMarch = styled.span`
  font-family: ${t.font}; font-size: 0.645rem; font-weight: 540;
  color: ${t.textSoft}; background: ${t.surfaceSunk};
  padding: 2px 7px; border-radius: ${t.rXs}; white-space: nowrap;
`
/* Ligne de méta des devis consultés — accentuée si la consultation est à traiter */
const SeenMeta = styled.div`
  font-family: ${t.font}; font-size: 0.7rem; margin-top: 4px;
  font-variant-numeric: tabular-nums;
  font-weight: ${(p) => (p.$on ? 570 : 480)};
  color: ${(p) => (p.$on ? t.gold : t.textSoft)};
  span { color: ${t.textFaint}; font-weight: 480; }
`
/* Compteur cliquable de l'en-tête : bascule le filtre « consultés » */
const CounterBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  font-family: ${t.font}; font-size: 0.735rem; font-weight: 540;
  letter-spacing: -0.008em;
  color: ${t.gold}; background: ${t.goldWash};
  border: 1px solid #CFE2DC; border-radius: ${t.rSm};
  padding: 4px 10px; cursor: pointer;
  transition: border-color 0.14s, background 0.14s;
  &:hover { border-color: ${t.gold}; }
  em {
    font-style: normal; font-size: 0.68rem; font-weight: 500;
    color: ${t.textFaint}; border-left: 1px solid #CFE2DC; padding-left: 8px;
  }
`

/* Deuxième axe de tri : un intitulé le sépare des statuts, sans quoi les
   deux rangées de pastilles se liraient comme une seule liste de filtres
   qui s'excluent. */
const BarreMarchandise = styled.div`
  display: flex; align-items: center; gap: 10px;
  margin: -8px 0 18px;
  /* Sans min-width, la rangée de pastilles pousse la ligne au lieu de défiler. */
  > div { min-width: 0; flex: 1; }
  @media (max-width: 700px) { align-items: stretch; flex-direction: column; gap: 6px; }
`
const IntituleAxe = styled.span`
  font-family: ${t.font}; font-size: 0.63rem; font-weight: 620;
  letter-spacing: 0.09em; text-transform: uppercase;
  color: ${t.textFaint}; white-space: nowrap; flex-shrink: 0;
`

/* Recherche libre : repliée derrière une loupe, elle ne prend de place
   que lorsqu'on s'en sert. */
const Loupe = styled(IconBtn)`
  ${(p) => p.$on && `color: ${t.gold}; border-color: ${t.gold}; background: ${t.goldWash};`}
`
const BarreRecherche = styled.div`
  margin: -6px 0 16px;
  animation: ${fadeUp} 0.18s cubic-bezier(.22,.9,.3,1) both;
`
const ChampRecherche = styled.div`
  position: relative;
  input { padding-right: 34px; font-size: 16px; } /* 16px : iOS ne zoome pas au focus */
`
const EffacerRecherche = styled.button`
  position: absolute; right: 5px; top: 50%; transform: translateY(-50%);
  width: 26px; height: 26px; border-radius: ${t.rXs};
  border: none; background: transparent; cursor: pointer;
  color: ${t.textFaint}; font-size: 0.85rem; line-height: 1;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: ${t.surfaceSunk}; color: ${t.text}; }
  &:focus-visible { outline: 2px solid ${t.gold}; outline-offset: 1px; }
`
const ResumeRecherche = styled.div`
  font-family: ${t.font}; font-size: 0.72rem; color: ${t.textFaint};
  margin-top: 6px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  font-variant-numeric: tabular-nums;
`

export default function DevisAdmin() {
  const navigate = useNavigate()
  const [quotes, setQuotes] = useState([])
  const [viewsByQuote, setViewsByQuote] = useState({})
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [marchandise, setMarchandise] = useState('all')
  const [rechercheOuverte, setRechercheOuverte] = useState(false)
  const [recherche, setRecherche] = useState('')

  useEffect(() => { fetchQuotes() }, [])

  const fetchQuotes = async () => {
    setLoading(true)
    const [{ data: qData }, { data: vData }] = await Promise.all([
      supabase.from('quotes').select('*').order('created_at', { ascending: false }),
      supabase.from('quote_views').select('quote_id, viewed_at').order('viewed_at', { ascending: false }),
    ])
    setQuotes(qData || [])
    const map = {}
    for (const v of (vData || [])) {
      if (!map[v.quote_id]) map[v.quote_id] = { count: 0, lastAt: v.viewed_at, dates: [] }
      map[v.quote_id].count++
      map[v.quote_id].dates.push(v.viewed_at)
    }
    setViewsByQuote(map)
    setLoading(false)
  }

  /* Consultation postérieure à ma dernière ouverture du dossier.
     Borne de 30 jours : sans elle, un devis jamais ouvert côté admin resterait
     signalé indéfiniment, même pour une consultation vieille de plusieurs mois. */
  const vuDepuis = (q) => {
    const v = viewsByQuote[q.id]
    if (!v) return false
    const last = new Date(v.lastAt)
    if (Date.now() - last > FRAIS_MS) return false
    return !q.admin_viewed_at || last > new Date(q.admin_viewed_at)
  }

  /* Consulté récemment, que j'aie déjà ouvert le dossier ou non. */
  const vuRecemment = (q) => {
    const v = viewsByQuote[q.id]
    return Boolean(v && Date.now() - new Date(v.lastAt) < RECENT_MS)
  }

  /* Jamais ouvert côté admin : la demande vient d'arriver. */
  const estNouveau = (q) => !q.admin_viewed_at

  /* Ouverte mais pas encore aboutie : tant qu'aucun devis n'est parti,
     la demande reste à traiter, y compris une fois marquée « Demande
     reçue » ou « Prise en charge ». Seul l'envoi du devis la solde. */
  const aTraiter = (q) => Boolean(q.admin_viewed_at) && !q.devis_content && q.status !== 'ready'

  /* Un seul liseré par ligne : le nouveau prime sur le à-traiter. */
  const markOf = (q) => (estNouveau(q) ? 'new' : aTraiter(q) ? 'todo' : undefined)

  /* Les deux filtres se croisent au lieu de se remplacer : « le foin
     encore à traiter » est la question qu'on se pose vraiment. */
  const trouves = recherche.trim() ? quotes.filter((q) => correspondRecherche(q, recherche)) : quotes

  const parEtat =
    filter === 'all' ? trouves
    : filter === 'nouveaux' ? trouves.filter(estNouveau)
    : filter === 'atraiter' ? trouves.filter(aTraiter)
    : filter === 'vus' ? trouves.filter(vuDepuis)
    : filter === 'recents' ? trouves.filter(vuRecemment)
    : trouves.filter((q) => q.status === filter)

  const filtered = marchandise === 'all'
    ? parEtat
    : parEtat.filter((q) => categoriesDe(q).includes(marchandise))

  /* Une famille jamais demandée n'a pas à occuper la barre. */
  const categoriesPresentes = CATEGORIES.filter((c) => quotes.some((q) => categoriesDe(q).includes(c.key)))
  const nbParCategorie = (key) => parEtat.filter((q) => categoriesDe(q).includes(key)).length

  const nbNouveaux = quotes.filter(vuDepuis).length
  const nbRecents = quotes.filter(vuRecemment).length
  const nbNeufs = quotes.filter(estNouveau).length
  const nbATraiter = quotes.filter(aTraiter).length

  const enRecherche = Boolean(recherche.trim())
  const filtresActifs = filter !== 'all' || marchandise !== 'all'
  const fermerRecherche = () => { setRecherche(''); setRechercheOuverte(false) }

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>
            Devis
            {nbNouveaux > 0 && (
              <CounterBtn
                onClick={() => setFilter(filter === 'vus' ? 'all' : 'vus')}
                title="N'afficher que ces demandes"
              >
                👁 {nbNouveaux} consulté{nbNouveaux > 1 ? 's' : ''} depuis votre dernière visite
                <em>{filter === 'vus' ? 'voir tout' : 'filtrer'}</em>
              </CounterBtn>
            )}
          </PageTitle>
          <PageSub>
            {quotes.length} demande(s)
            {nbNeufs > 0 && ` · ${nbNeufs} nouvelle${nbNeufs > 1 ? 's' : ''}`}
            {nbATraiter > 0 && ` · ${nbATraiter} à traiter`}
          </PageSub>
        </div>
        <Loupe type="button" $on={rechercheOuverte} aria-expanded={rechercheOuverte}
          aria-label={rechercheOuverte ? 'Fermer la recherche' : 'Rechercher un devis'}
          title="Rechercher un contact"
          onClick={() => (rechercheOuverte ? fermerRecherche() : setRechercheOuverte(true))}>
          🔍
        </Loupe>
      </PageHead>

      {rechercheOuverte && (
        <BarreRecherche>
          <ChampRecherche>
            <Input type="search" autoFocus value={recherche} aria-label="Rechercher un devis"
              placeholder="Nom, prénom, téléphone, email, localité…"
              onChange={(e) => setRecherche(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') fermerRecherche() }} />
            {recherche && (
              <EffacerRecherche type="button" aria-label="Effacer la recherche" onClick={() => setRecherche('')}>✕</EffacerRecherche>
            )}
          </ChampRecherche>
          {enRecherche && !loading && (
            <ResumeRecherche>
              {filtered.length} devis trouvé{filtered.length > 1 ? 's' : ''}
              {/* Le filtre actif peut cacher le contact qu'on cherche : on le dit. */}
              {filtresActifs && trouves.length > filtered.length && (
                <Btn $variant="quiet" $size="sm" onClick={() => { setFilter('all'); setMarchandise('all') }}>
                  +{trouves.length - filtered.length} hors filtre — tout afficher
                </Btn>
              )}
            </ResumeRecherche>
          )}
        </BarreRecherche>
      )}

      <ChipBar $scroll style={{ marginBottom: 18 }}>
        <Chip $on={filter === 'all'} onClick={() => setFilter('all')}>
          Tous <span>{quotes.length}</span>
        </Chip>
        {nbNeufs > 0 && (
          <Chip $on={filter === 'nouveaux'} onClick={() => setFilter('nouveaux')}>
            ● Nouveaux <span>{nbNeufs}</span>
          </Chip>
        )}
        {nbATraiter > 0 && (
          <Chip $on={filter === 'atraiter'} onClick={() => setFilter('atraiter')}>
            À traiter <span>{nbATraiter}</span>
          </Chip>
        )}
        {nbRecents > 0 && (
          <Chip $on={filter === 'recents'} onClick={() => setFilter('recents')}>
            👁 Consultés (7 j) <span>{nbRecents}</span>
          </Chip>
        )}
        {STATUS_ORDER.map((s) => (
          <Chip key={s} $on={filter === s} onClick={() => setFilter(s)}>
            {StatusLabels[s]} <span>{quotes.filter((q) => q.status === s).length}</span>
          </Chip>
        ))}
      </ChipBar>

      {categoriesPresentes.length > 0 && (
        <BarreMarchandise>
          <IntituleAxe>Marchandise</IntituleAxe>
          <ChipBar $scroll>
            <Chip $on={marchandise === 'all'} aria-pressed={marchandise === 'all'} onClick={() => setMarchandise('all')}>
              Toutes <span>{parEtat.length}</span>
            </Chip>
            {categoriesPresentes.map((c) => (
              <Chip key={c.key} $on={marchandise === c.key} aria-pressed={marchandise === c.key}
                onClick={() => setMarchandise(marchandise === c.key ? 'all' : c.key)}>
                {c.label} <span>{nbParCategorie(c.key)}</span>
              </Chip>
            ))}
          </ChipBar>
        </BarreMarchandise>
      )}

      {loading ? (
        <Loading rows={4} h="86px" />
      ) : filtered.length === 0 ? (
        <Empty
          icon={enRecherche ? '🔍'
            : marchandise !== 'all' ? CATEGORIES.find((c) => c.key === marchandise).label.split(' ')[0]
            : filter === 'vus' || filter === 'recents' ? '👁' : filter === 'nouveaux' || filter === 'atraiter' ? '✅' : '📋'}
          title={
            enRecherche ? 'Aucun devis trouvé'
            : marchandise !== 'all' ? 'Aucune demande pour cette marchandise'
            : filter === 'vus' ? 'Rien de nouveau à regarder'
            : filter === 'recents' ? 'Aucune consultation cette semaine'
            : filter === 'nouveaux' ? 'Aucune demande en attente'
            : filter === 'atraiter' ? 'Rien en souffrance'
            : 'Aucun devis ici'
          }
        >
          {enRecherche
            ? <>Rien ne correspond à « {recherche.trim()} »{filtresActifs && trouves.length > 0 && ' avec ce filtre'}. <Chip onClick={() => setRecherche('')}>Effacer la recherche</Chip></>
            : marchandise !== 'all'
            ? <>Rien ici en croisant avec ce filtre. <Chip onClick={() => setMarchandise('all')}>Toutes les marchandises</Chip></>
            : filter === 'all'
            ? "Les demandes envoyées depuis le formulaire du site apparaîtront dans cette liste."
            : filter === 'vus'
            ? "Personne n'a ouvert sa page de suivi depuis votre dernier passage sur le dossier."
            : filter === 'recents'
            ? "Aucun client n'a ouvert sa page de suivi ces sept derniers jours."
            : filter === 'nouveaux'
            ? 'Vous avez ouvert toutes les demandes reçues.'
            : filter === 'atraiter'
            ? 'Toutes les demandes ouvertes ont reçu leur devis.'
            : 'Aucune demande avec ce statut pour le moment.'}
        </Empty>
      ) : (
        filtered.map((q, i) => {
          const v = viewsByQuote[q.id]
          const isNew = vuDepuis(q)
          // true = lu après envoi · false = envoyé mais pas lu · null = non concerné
          const lu = !q.devis_content || !q.devis_sent_at
            ? null
            : repartitionVues(v?.dates, q.devis_sent_at).apres > 0
          return (
            <Stagger key={q.id} $i={i}>
              <ListItem $mark={markOf(q)} onClick={() => navigate(`/admin/devis/${q.id}`)}>
                <ItemRow>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Cluster $gap="7px">
                      <ItemTitle>{q.name}</ItemTitle>
                      {estNouveau(q) && <TagNeuf>● Nouveau</TagNeuf>}
                      {aTraiter(q) && <TagTodo>À traiter</TagTodo>}
                      {lu === true && <TagLu>👁 Devis lu</TagLu>}
                      {lu === false && <TagNonLu>Devis non lu</TagNonLu>}
                      {lu === null && isNew && <NewFlag>👁 Consulté</NewFlag>}
                      {categoriesDe(q).map((key) => (
                        <TagMarch key={key}>{CATEGORIES.find((c) => c.key === key).label}</TagMarch>
                      ))}
                    </Cluster>
                    <ItemSub>{q.email}{enRecherche && q.phone && ` · ${q.phone}`}</ItemSub>
                    {v ? (
                      <SeenMeta $on={isNew}>
                        👁 Consulté {depuis(v.lastAt)} — {fmtDateTime(v.lastAt)}
                        <span> · demande du {fmtDate(q.created_at)}</span>
                      </SeenMeta>
                    ) : (
                      <ItemMeta>{fmtDateTime(q.created_at)}</ItemMeta>
                    )}
                  </div>
                  <ItemSide>
                    <Pill $tone={StatusTone[q.status]}>{StatusLabels[q.status]}</Pill>
                    {v && (
                      <ViewTag $new={isNew} title={`Dernière consultation : ${fmtDateTime(v.lastAt)}`}>
                        👁 {v.count}×
                      </ViewTag>
                    )}
                  </ItemSide>
                </ItemRow>
              </ListItem>
            </Stagger>
          )
        })
      )}
    </>
  )
}
